import { Configuration, PlaidApi, PlaidEnvironments } from 'plaid';

// Get environment variables
const PLAID_CLIENT_ID = import.meta.env.VITE_PLAID_CLIENT_ID;
const PLAID_SECRET = import.meta.env.VITE_PLAID_SECRET;

if (!PLAID_CLIENT_ID || !PLAID_SECRET) {
  throw new Error('Missing required Plaid environment variables');
}

const configuration = new Configuration({
  basePath: PlaidEnvironments.sandbox,
  baseOptions: {
    headers: {
      'PLAID-CLIENT-ID': PLAID_CLIENT_ID,
      'PLAID-SECRET': PLAID_SECRET,
    },
  },
});

const plaidClient = new PlaidApi(configuration);

export const createLinkToken = async () => {
  try {
    const response = await plaidClient.linkTokenCreate({
      user: { 
        // Generate a unique ID for each user
        client_user_id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` 
      },
      client_name: 'Angel Answer Engine',
      products: ['transactions', 'auth'],
      country_codes: ['US'],
      language: 'en',
      webhook: 'https://webhook.example.com/plaid', // Update with your webhook URL
    });
    return response.data.link_token;
  } catch (error) {
    console.error('Error creating Plaid link token:', error);
    if (error.response) {
      throw new Error(`Plaid API error: ${error.response.data.error_message}`);
    }
    throw error;
  }
};

export const exchangePublicToken = async (publicToken: string) => {
  try {
    const response = await plaidClient.itemPublicTokenExchange({
      public_token: publicToken,
    });
    return response.data.access_token;
  } catch (error) {
    console.error('Error exchanging public token:', error);
    throw error;
  }
};

export const getTransactions = async (accessToken: string) => {
  try {
    const now = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(now.getDate() - 30);

    const response = await plaidClient.transactionsGet({
      access_token: accessToken,
      start_date: thirtyDaysAgo.toISOString().split('T')[0],
      end_date: now.toISOString().split('T')[0],
      options: {
        include_personal_finance_category: true,
      }
    });

    return response.data.transactions;
  } catch (error) {
    console.error('Error fetching transactions:', error);
    throw error;
  }
};

export const getAccounts = async (accessToken: string) => {
  try {
    const response = await plaidClient.accountsGet({
      access_token: accessToken,
    });
    return response.data.accounts;
  } catch (error) {
    console.error('Error fetching accounts:', error);
    throw error;
  }
};

// Get account balances
export const getBalances = async (accessToken: string) => {
  try {
    const response = await plaidClient.accountsBalanceGet({
      access_token: accessToken,
    });
    return response.data.accounts;
  } catch (error) {
    console.error('Error fetching balances:', error);
    throw error;
  }
};

// Get account identity information
export const getIdentity = async (accessToken: string) => {
  try {
    const response = await plaidClient.identityGet({
      access_token: accessToken,
    });
    return response.data.accounts;
  } catch (error) {
    console.error('Error fetching identity:', error);
    throw error;
  }
};